import { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import api_endpoints from "../../api";
import PredefinedPeriods from "../../widgets/CustomDatePicker/PredefinedPeriod";
import DataGrid from "../../widgets/DataGrid/DataGrid";
import LoadingContainer from "../LoadingContainer/LoadingContainer";
import {
  Divider,
  Select,
  MenuItem,
  useTheme,
  Chip,
  Button,
  TextField,
  Typography,
} from "@mui/material";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import Tooltip from "@mui/material/Tooltip";
import {
  useApiClientQuery,
  useApiClientMutation,
} from "../../hooks/useApiClient";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import AuthContext from "../../context/AuthContext/AuthContext";
import {
  dateTimeReadable,
  numberWithCommas,
  dateTimeReadableSimple,
  formatDateTime,
} from "../../utils";
import ButtonLoading from "../../widgets/ButtonLoading/ButtonLoading";
import { useSuccessAlert } from "../../context/SuccessAlertContext/SuccessAlertContext";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TableVirtuoso from "../../widgets/TableVirtuoso/TableVirtuoso";
import CustomTextField from "../../widgets/CustomTextField/CustomTextField";
import CustomSelect from "../../widgets/CustomSelect/CustomSelect";
import dayjs from "dayjs";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useCustomTranslation } from "../../hooks/useCustomTranslation";
import { TRANSACTION_PAYMENT_TYPE } from "../../enums/Enums";
import CancelButton from "../../widgets/CancelButton/CancelButton";
import ReceiptsByYearTable from "./ReceiptsByYearTable";
import PaymentMethodsTab from "./PaymentMethodsTab";
import PromiseFollowUpTab from "./PromiseFollowUpTab";
import ReceiptCampaignDialog from "./ReceiptCampaignDialog";

const Donor = () => {
  const authContext = useContext(AuthContext);
  const params = useParams();
  const theme = useTheme();
  let emailParam;
  try {
    emailParam = decodeURIComponent(params.email || "");
  } catch (err) {
    console.warn("Failed to decode email parameter:", err);
    emailParam = params.email || "";
  }
  const [addCashDialogOpen, setAddCashDialogOpen] = useState(false);
  const [campaignsMenuOption, setCampaignsMenuOption] = useState([]);
  const [activeDonorCampaignsMenuOption, setActiveDonorCampaignsMenuOption] =useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("this_year");
  const [yearOptions, setYearOptions] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [receiptStartDate, setReceiptStartDate] = useState(dayjs().startOf("year"));
  const [receiptEndDate, setReceiptEndDate] = useState(dayjs().endOf("year"));
  const [receiptPreviewData, setReceiptPreviewData] = useState(null);
  const [receiptPreviewLoading, setReceiptPreviewLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(null);
  const { t } = useCustomTranslation("Donor Profile");
  const { t: tPM } = useCustomTranslation("Payment Methods"); 
  const navigate = useNavigate();

  const {
    showSuccessAlert,
    setShowSuccessAlert,
    messageSuccessAlert,
    setMessageSuccessAlert,
  } = useSuccessAlert();

  const [tableFilter, setTableFilter] = useState({
    organization_id: authContext.authContext.accessToken.organization_id,
    amount: "",
    payment_type: "",
    campaignId: "",
    date: dayjs(new Date()),
    start_date: dayjs().startOf("year"),
    end_date: dayjs(),
    start_time: dayjs().startOf("day").format("HH:mm"),
    end_time: dayjs().endOf("day").format("HH:mm"),
  });

  const [addCashData, setAddCashData] = useState({
    amount: "",
    campaignId: "",
    donationDate: dayjs(new Date()),
  });

  const columns = [
    {
      field: "campaignName",
      headerName: t("donor_profile_transactions_table_campaign"),
      sortable: true,
      numberSort: false,
    },
    {
      field: "amount",
      headerName: t("donor_profile_transactions_table_amount"),
      sortable: true,
      numberSort: true,
    },
    {
      field: "payment_type",
      headerName: t("payment_type"),
      sortable: true,
      numberSort: false,
      renderCell: (params) => {
        const isRefund = params.row.isRefund;
        const paymentType = params.value;

        const getPaymentTypeColor = (type, isRefund) => {
          if (isRefund) return "error";
          const normalizedType = (type || "").toLowerCase();
          if (normalizedType === "cash") return "success";
          if (["card", "credit_card", "debit_card"].includes(normalizedType)) {
            return "primary";
          }
          return "default";
        };

        const getPaymentTypeLabel = (type) => {
          switch ((type || "").toLowerCase()) {
            case TRANSACTION_PAYMENT_TYPE.CASH:
              return t("cash", "Cash");
            case TRANSACTION_PAYMENT_TYPE.CARD:
              return t("card", "Card");
            case TRANSACTION_PAYMENT_TYPE.CREDIT_CARD:
              return t("credit_card", "Credit Card");
            case TRANSACTION_PAYMENT_TYPE.DEBIT_CARD:
              return t("debit_card", "Debit Card");
            default:
              return type || "Unknown";
          }
        };

        const safeType = getPaymentTypeLabel(paymentType);
        const label = isRefund ? `Refund - ${safeType}` : safeType;

        return (
          <Chip
            label={label}
            color={getPaymentTypeColor(paymentType, isRefund)}
            size="small"
            sx={{ fontWeight: 500 }}
          />
        );
      },
    },
    {
      field: "date",
      headerName: t("donor_profile_transactions_table_period"),
      sortable: true,
      numberSort: false,
    },
  ];

  const receiptListColumns = [
    {
      field: "year",
      headerName: t("donor_profile_year_text"),
      sortable: true,
      numberSort: false,
    },
    {
      field: "status",
      headerName: t("donor_profile_status_text"),
      sortable: true,
      numberSort: false,
      flex: 1,
      renderCell: (params) => params.row.status?.display,
    },
    {
      field: "created_at",
      headerName: t("created_at"),
      sortable: false,
      numberSort: false,
      renderCell: (param) =>
        dateTimeReadableSimple(new Date(param.row.created_at)),
    },
    {
      field: "created_by",
      headerName: t("created_by"),
      sortable: false,
      numberSort: false,
      renderCell: (param) => {
        const user = param.row.created_by;
        if (user && user.first_name && user.last_name) {
          return `${user.first_name} ${user.last_name}`;
        }
        return "User not identified";
      },
    },
  ];

  const [receiptCampaignDialogOpen, setReceiptCampaignDialogOpen] = useState(false);
  const [receiptCampaignSearch, setReceiptCampaignSearch] = useState("");
  const [selectedReceiptCampaignIds, setSelectedReceiptCampaignIds] = useState([]);
  const [receiptCampaignSelectionError, setReceiptCampaignSelectionError] = useState("");

  const resetReceiptCampaignStep = () => {
    setReceiptCampaignDialogOpen(false);
    setReceiptCampaignSearch("");
    setSelectedReceiptCampaignIds([]);
    setReceiptCampaignSelectionError("");
    setReceiptPreviewData(null);
  };

  
  const donorQuery = useApiClientQuery(
    () => api_endpoints.getDonor(emailParam, authContext.authContext.accessToken.organization_id),
    {
      queryKey: ["donor_data", emailParam, authContext.authContext.accessToken.organization_id],
    }
  );

  const receiptCampaignOptionsQuery = useApiClientQuery(
    () =>
      api_endpoints.getReceiptCampaigns(
        donorQuery.data._id,
        authContext.authContext.accessToken.organization_id,
        selectedYear,
        receiptStartDate?.startOf("day").toISOString(),
        receiptEndDate?.endOf("day").toISOString()
      ),
    {
      queryKey: [
        "donor_receipt_campaigns",
        donorQuery.data?._id,
        authContext.authContext.accessToken.organization_id,
        selectedYear,
        receiptStartDate?.format("YYYY-MM-DD"),
        receiptEndDate?.format("YYYY-MM-DD"),
      ],
      enabled: receiptCampaignDialogOpen && !!donorQuery.data,
    }
  );

  useEffect(() => {
    if (receiptCampaignDialogOpen && receiptCampaignOptionsQuery.data) {
      setSelectedReceiptCampaignIds(
        receiptCampaignOptionsQuery.data.map((campaign) => campaign._id)
      );
      setReceiptCampaignSelectionError("");
    }
  }, [receiptCampaignDialogOpen, receiptCampaignOptionsQuery.data, selectedYear]);

  useEffect(() => {
    const start = dayjs(`${selectedYear}-01-01`).startOf("day");
    const end = dayjs(`${selectedYear}-12-31`).endOf("day");
    setReceiptStartDate(start);
    setReceiptEndDate(end);
  }, [selectedYear]);

  const handleOpenReceiptCampaignDialog = () => {
    setReceiptCampaignDialogOpen(true);
    setReceiptCampaignSearch("");
    setReceiptCampaignSelectionError("");
  };

  const handleToggleReceiptCampaign = (campaignId) => {
    setReceiptCampaignSelectionError("");
    setSelectedReceiptCampaignIds((current) =>
      current.includes(campaignId)
        ? current.filter((id) => id !== campaignId)
        : [...current, campaignId]
    );
  };

  const handleSelectAllReceiptCampaigns = () => {
    setReceiptCampaignSelectionError("");
    setSelectedReceiptCampaignIds(
      (receiptCampaignOptionsQuery.data || []).map((campaign) => campaign._id)
    );
  };

  const handleClearReceiptCampaigns = () => {
    setSelectedReceiptCampaignIds([]);
  };


  const [viewingReceiptId, setViewingReceiptId] = useState(null);

  const viewReceiptMutation = useApiClientMutation(
    ({ receiptId }) => api_endpoints.getReceiptContent(receiptId),
    [],
    async (payload) => {
      try {
        if (payload?.type === "url") {
          const receiptId = payload.url.split("/").slice(-2)[0]; // "/api/donor/receipts/:id/pdf"
          const resp = await api_endpoints.getReceiptPdf(receiptId);
          const blob = resp.data;
          const url = URL.createObjectURL(blob);
          setPdfBlobUrl(url);
          setPdfDialogOpen(true);
          return;
        }

        if (payload?.type === "base64" && payload.base64) {
          const bin = atob(payload.base64);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
          const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
          setPdfBlobUrl(url);
          setPdfDialogOpen(true);
          return;
        }
      } catch (e) {
        console.error("Unable to open receipt PDF:", e);
      }
    }
  );

  const softDeleteReceiptMutation = useApiClientMutation(
    ({ receiptId, delete_after, deleted_reason }) =>
      api_endpoints.softDeleteReceipt(receiptId, { delete_after, deleted_reason }),
    ["donor_receipts_grouped"],
    () => {
      donorReceiptsGroupedQuery.refetch?.();
      setMessageSuccessAlert("Receipt scheduled for deletion");
      setShowSuccessAlert(true);
    }
  );

  const restoreReceiptMutation = useApiClientMutation(
    ({ receiptId }) => api_endpoints.restoreReceipt(receiptId),
    ["donor_receipts_grouped"],
    () => {
      donorReceiptsGroupedQuery.refetch?.();
      setMessageSuccessAlert("Receipt restored");
      setShowSuccessAlert(true);
    }
  );

  const handleDeleteReceipt = (r) => {
    const days = 30; // grace period
    const deleteAfter = new Date(Date.now() + days*24*60*60*1000).toISOString();
    softDeleteReceiptMutation.mutate({ receiptId: r._id, delete_after: deleteAfter, deleted_reason: "" });
  };

  const handleRestoreReceipt = (r) => {
    restoreReceiptMutation.mutate({ receiptId: r._id });
  };

  const handlePeriodChange = (event) => {
    const value = event.target.value;
    setSelectedPeriod(value);
  };

  const setpersonalizedDates = (startDate, endDate, starttime, endtime) => {
    setTableFilter({
      ...tableFilter,
      start_date: startDate,
      end_date: endDate,
      start_time: starttime.format("HH:mm"),
      end_time: endtime.format("HH:mm"),
    });
  };

  const filters = {
    amount: (
      <CustomTextField
        value={tableFilter.amount || ""}
        onChange={(e) => changeTableFilter("amount", e.target.value)}
        clearFilter={() => clearTableFilter("amount")}
      />
    ),
    campaignName: (
      <CustomSelect
        value={tableFilter.campaignId}
        onChange={(e) => {
          changeTableFilter("campaignId", e.target.value);
        }}
        options={campaignsMenuOption}
      />
    ),
    payment_type: (
      <CustomSelect
        value={tableFilter.payment_type}
        onChange={(e) => {
          changeTableFilter("payment_type", e.target.value);
        }}
        options={[
          { value: "", label: t("all_text") },
          {
            value: TRANSACTION_PAYMENT_TYPE.CASH,
            label: t("cash"),
          },
          {
            value: TRANSACTION_PAYMENT_TYPE.CARD,
            label: t("card", "Card"),
          },
          {
            value: TRANSACTION_PAYMENT_TYPE.CREDIT_CARD,
            label: t("credit_card", "Credit Card"),
          },
          {
            value: TRANSACTION_PAYMENT_TYPE.DEBIT_CARD,
            label: t("debit_card", "Debit Card"),
          },
        ]}
      />
    ),
    date: (
      <PredefinedPeriods
        selectedPeriod={selectedPeriod}
        handlePeriodChange={handlePeriodChange}
        setSelectedPeriod={setSelectedPeriod}
        setpersonalizedDates={setpersonalizedDates}
      />
    ),
  };

  useEffect(() => {
    createYearOptionsReceipt();
  }, []);

  const changeTableFilter = (option, value) => {
    if (option === "amount") {
      // Allow only numbers and ignore any non-numeric input
      const numericValue = value.replace(/[^0-9]/g, "");

      setTableFilter((prevFilter) => ({
        ...prevFilter,
        [option]: numericValue,
      }));
    } else {
      // For other options, set the value as it is
      setTableFilter((prevFilter) => ({
        ...prevFilter,
        [option]: value,
      }));
    }
  };

  const clearTableFilter = (option) => {
    setTableFilter((prevFilter) => ({ ...prevFilter, [option]: "" }));
  };

  const changeAddCashData = (option, value) => {
    if (option === "amount") {
      const numericValue = value
        .replace(/[^0-9.]/g, "") // Allow only numbers and a decimal point
        .replace(/(\..*?)\..*/g, "$1") // Ensure only one decimal point
        .replace(/(\.\d{2})\d+/, "$1"); // Limit to 2 decimal places

      setAddCashData((prevFilter) => ({
        ...prevFilter,
        [option]: numericValue,
      }));
    } else {
      // For other options, set the value as it is
      setAddCashData((prevFilter) => ({
        ...prevFilter,
        [option]: value,
      }));
    }
  };

  const donorLastReceiptStatusQuery = useApiClientQuery(
    () =>
      api_endpoints.getDonorLastReceiptStatus(
        donorQuery.data._id,
        authContext.authContext.accessToken.organization_id
      ),
    {
      queryKey: [
        'donor_last_receipt_status',
        donorQuery.data?._id,
        authContext?.authContext?.accessToken?.organization_id,
        selectedYear,
      ],
      enabled:
        Boolean(donorQuery.data?._id) &&
        Boolean(selectedYear) &&
        Boolean(authContext?.authContext?.accessToken?.organization_id),

      refetchInterval: (data) => (data?.some?.((r) => r?.status === 'processing') ? 2000 : false),

      refetchOnWindowFocus: false,
    }
  );

  const lastCurrentYearReceiptStatus = donorLastReceiptStatusQuery.data?.find(
    (receipt) => selectedYear === receipt.year
  );

  
  useEffect(() => {
    if (donorQuery.data) {
      setForm({
        first_name: donorQuery.data.first_name || "",
        last_name: donorQuery.data.last_name || "",
        email: donorQuery.data.email || "",
        phone: donorQuery.data.phone || "",
        adresse: donorQuery.data.adresse || "",
        company: donorQuery.data.company || "",
        zip_code: donorQuery.data.zip_code || "",
        organization_id: donorQuery.data.organization_id || undefined,
      });
    }
  }, [donorQuery.data]);

  const donorActiveCampaignsQuery = useApiClientQuery(
    () =>
      api_endpoints.getCampaigns(
        authContext.authContext.accessToken.organization_id,
        "",
        "",
        "",
        true,
        "",
        false,
        false
      ),
    {
      queryKey: [
        `donor_activeCampaign_organizationid_donorEmail${tableFilter.organization_id}_${params.email}`,
      ],
      enabled: addCashDialogOpen,
    },
    (data) => {
      const options = data.map((campaign) => ({
        value: campaign._id,
        label: (
          <div
            key={campaign._id}
            className="flex flex-1 items-center justify-between"
          >
            <span>{campaign.name}</span>
            <div>
              <Chip
                color={campaign.enabled ? "success" : "error"}
                label={
                  campaign.enabled
                    ? t("donor_profile_active_status")
                    : t("donor_profile_inActive_status")
                }
                size="small"
              />
            </div>
          </div>
        ),
      }));

      // Set addCashData.campaignId to the first option if not already set
      if (!addCashData.campaignId && options.length > 0) {
        changeAddCashData("campaignId", options[0].value);
      }

      // Update state
      setActiveDonorCampaignsMenuOption(options);
    }
  );

  const campaignFilterQuery = useApiClientQuery(
    () =>
      api_endpoints.getCampaigns(
        authContext.authContext.accessToken.organization_id
      ),
    {
      queryKey: [
        `donor_campaignFilter_organizationid_${tableFilter.organization_id}`,
      ],
    },
    (data) => {
      const options = data.map((campaign) => ({
        value: campaign._id,
        label: (
          <div
            key={campaign._id}
            className="flex flex-1 items-center justify-between"
          >
            <span>{campaign.name}</span>
            <div>
              <Chip
                color={campaign.enabled ? "success" : "error"}
                label={
                  campaign.enabled
                    ? t("donor_profile_active_status")
                    : t("donor_profile_inActive_status")
                }
                size="small"
              />
            </div>
          </div>
        ),
      }));

      // Add "Tout" option at the beginning
      options.unshift({ value: "", label: t("donor_profile_all_option") });

      // Update state
      setCampaignsMenuOption(options);
    }
  );

  const transactionsQuery = useApiClientQuery(
    () =>
      api_endpoints.getDonorTransactions(
        authContext.authContext.accessToken.organization_id,
        emailParam,
        tableFilter.amount,
        tableFilter.campaignId,
        formatDateTime(tableFilter.start_date, tableFilter.start_time),
        formatDateTime(tableFilter.end_date, tableFilter.end_time),
        tableFilter.payment_type
      ),
    {
      queryKey: [
        "donor_transactions",
        emailParam,
        tableFilter,
        authContext.authContext.accessToken.organization_id,
      ],
    }
  );

  const generateReceiptMutation = useApiClientMutation(
    api_endpoints.generateReceipt,
    [["donor_last_receipt_status"], ["donor_receipts_grouped"]],
    (data) => {
      resetReceiptCampaignStep();
      setMessageSuccessAlert(t(data));
      setShowSuccessAlert(true);
    }
  );

  const addDonorCashMutation = useApiClientMutation(
    api_endpoints.addCashToCampaign,
    [`donor_transactions`],
    (data) => {
      setMessageSuccessAlert(data);
      setShowSuccessAlert(true);
      transactionsQuery.refetch();
      handleCloseModal();
    }
  );

  const updateDonorMutation = useApiClientMutation(
    ({ id, body }) => api_endpoints.updateDonorById(id, body),
    ["donor_data", params.email],
    (updatedDonor) => {
      setForm({
        first_name: updatedDonor.first_name || "",
        last_name: updatedDonor.last_name || "",
        email: updatedDonor.email || "",
        phone: updatedDonor.phone || "",
        adresse: updatedDonor.adresse || "",
        company: updatedDonor.company || "",
        zip_code: updatedDonor.zip_code || "",
        organization_id: updatedDonor.organization_id || undefined,
      });

      donorQuery.refetch();

      setIsEditing(false);

      if (updatedDonor.email && updatedDonor.email !== emailParam) {
        navigate(`/donors/${encodeURIComponent(updatedDonor.email)}`);
      }
      setMessageSuccessAlert(t("donor_profile_updated_success"));
      setShowSuccessAlert(true);
    }
  );

  const resendLinkMutation = useApiClientMutation(
    ({ id }) => api_endpoints.resendDonorProfileLink(id),
    [],
    () => {
      setMessageSuccessAlert(t("donor_profile_link_sent"));
      setShowSuccessAlert(true);
    }
  );

  const handleAddCashToCampaign = async (e) => {
    e.preventDefault();
    addDonorCashMutation.mutate({
      campaign_id: addCashData.campaignId,
      cash_amount: addCashData.amount,
      inserted_by: authContext.authContext.accessToken.id,
      organization_id: authContext.authContext.accessToken.organization_id,
      date_donated: formatDateTime(addCashData.donationDate),
      donor_id: donorQuery.data._id,
    });
  };

  const buildReceiptData = () => ({
    donor_id: donorQuery.data._id,
    organization_id: authContext.authContext.accessToken.organization_id,
    year: selectedYear,
    campaign_ids: selectedReceiptCampaignIds,
    start_date: receiptStartDate?.startOf("day").toISOString(),
    end_date: receiptEndDate?.endOf("day").toISOString(),
  });

  const previewReceipt = async () => {
    if (selectedReceiptCampaignIds.length === 0) {
      setReceiptCampaignSelectionError(
        t("donor_receipt_campaign_selection_error_required", {
          fallback: "Select at least one campaign before generating the receipt.",
        })
      );
      return;
    }
    if (!receiptStartDate || !receiptEndDate || receiptEndDate.isBefore(receiptStartDate)) {
      setReceiptCampaignSelectionError(
        t("donor_receipt_period_error", { fallback: "Select a valid receipt period." })
      );
      return;
    }

    try {
      setReceiptPreviewLoading(true);
      const receiptData = buildReceiptData();
      const response = await api_endpoints.previewReceipt(receiptData);
      if (pdfBlobUrl && pdfBlobUrl.startsWith("blob:")) URL.revokeObjectURL(pdfBlobUrl);
      const url = URL.createObjectURL(response.data);
      setReceiptPreviewData(receiptData);
      setReceiptCampaignDialogOpen(false);
      setPdfBlobUrl(url);
      setPdfDialogOpen(true);
    } catch (error) {
      console.error("Unable to preview receipt", error);
      setReceiptCampaignSelectionError(
        t("donor_receipt_preview_error", { fallback: "Unable to preview the receipt." })
      );
    } finally {
      setReceiptPreviewLoading(false);
    }
  };

  const generateReceipt = async () => {
    const receiptData = receiptPreviewData || buildReceiptData();
    generateReceiptMutation.mutate(receiptData);
    setPdfDialogOpen(false);
    if (pdfBlobUrl && pdfBlobUrl.startsWith("blob:")) URL.revokeObjectURL(pdfBlobUrl);
    setPdfBlobUrl(null);
    setReceiptPreviewData(null);
  };

  const createYearOptionsReceipt = () => {
    let data = [];
    const minYear = 2024;
    let yearOptions = Number(new Date().getFullYear());
    const iterations = yearOptions - minYear;

    for (let i = 0; i <= iterations; i++) {
      data.push(yearOptions--);
    }

    setYearOptions(data);
  };

  const handleTabChange = (e, newValue) => {
    setTabValue(newValue);
  };

  const handleCloseModal = () => {
    setAddCashDialogOpen(false);

    setAddCashData({
      amount: "",
      campaignId: "",
      donationDate: dayjs(new Date()),
    });
    addDonorCashMutation.reset(); //rest error state, if any
  };

  const donorReceiptsGroupedQuery = useApiClientQuery(
    () => api_endpoints.getDonorReceiptsGrouped(donorQuery.data._id, authContext.authContext.accessToken.organization_id),
    {
      queryKey: ["donor_receipts_grouped", donorQuery.data?._id],
      enabled: !!donorQuery.data,
    }
  );

  const handleViewReceipt = async (receiptRow) => {
    setViewingReceiptId(receiptRow._id);
    viewReceiptMutation.mutate({ receiptId: receiptRow._id });
  }

  const setReceiptValidityMutation = useApiClientMutation(
    ({ receiptId, is_valid }) => api_endpoints.setReceiptValidity(receiptId, is_valid),
    ["donor_receipts_grouped"],
    () => donorReceiptsGroupedQuery.refetch?.()
  );

  const handleToggleValid = (r, is_valid) => {
    setReceiptValidityMutation.mutate({ receiptId: r._id, is_valid });
  };


  return (
    <LoadingContainer loading={donorQuery.isLoading} error={donorQuery.isError}>
      <Dialog fullWidth open={addCashDialogOpen} onClose={handleCloseModal}>
        <form onSubmit={handleAddCashToCampaign}>
          <DialogTitle>{t("donor_profile_addCash_button")}</DialogTitle>
          <LoadingContainer
            loading={
              addDonorCashMutation.isPending ||
              donorActiveCampaignsQuery.isLoading
            }
            error={
              addDonorCashMutation.isError || donorActiveCampaignsQuery.isError
            }
          >
            <DialogContent>
              <div className="m-2 flex flex-1 flex-col space-y-4">
                <CustomSelect
                  value={addCashData.campaignId}
                  onChange={(e) => {
                    changeAddCashData("campaignId", e.target.value);
                  }}
                  label={"Campaigns"}
                  options={activeDonorCampaignsMenuOption}
                  required={true}
                />
                <TextField
                  placeholder={t("donor_profile_insertAmount_text")}
                  label={t("donor_profile_insertAmount_text")}
                  type="text"
                  value={addCashData.amount}
                  onChange={(e) => {
                    changeAddCashData("amount", e.target.value);
                  }}
                  required
                />
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="fr-ca"
                >
                  <DatePicker
                    value={addCashData.donationDate}
                    onChange={(value) => {
                      changeAddCashData("donationDate", value.$d.getTime());
                    }}
                    label={t("donor_profile_donationDate_text")}
                    sx={{ width: "100%" }}
                    maxDate={dayjs(new Date())}
                    slotProps={{
                      textField: {
                        error: false,
                        required: true,
                      },
                    }}
                  />
                </LocalizationProvider>
              </div>
            </DialogContent>
          </LoadingContainer>
          <DialogActions>
            <Button
              variant="outlined"
              color="error"
              onClick={handleCloseModal} // Correctly close the modal
            >
              {t("donor_profile_cancel_button_text")}
            </Button>
            <Button
              color="primary"
              variant="contained"
              type="submit"
              disabled={addDonorCashMutation.isPending}
            >
              {t("donor_profile_add_button_text")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <ReceiptCampaignDialog
        t={t}
        open={receiptCampaignDialogOpen}
        onClose={() => {
          if (!generateReceiptMutation.isPending && !receiptPreviewLoading) {
            resetReceiptCampaignStep();
          }
        }}
        isGenerating={generateReceiptMutation.isPending}
        isLoading={receiptCampaignOptionsQuery.isLoading}
        isError={receiptCampaignOptionsQuery.isError}
        campaigns={receiptCampaignOptionsQuery.data || []}
        search={receiptCampaignSearch}
        onSearchChange={setReceiptCampaignSearch}
        selectedCampaignIds={selectedReceiptCampaignIds}
        onSelectAll={handleSelectAllReceiptCampaigns}
        onClearAll={handleClearReceiptCampaigns}
        onToggleCampaign={handleToggleReceiptCampaign}
        selectionError={receiptCampaignSelectionError}
        selectedYear={selectedYear}
        startDate={receiptStartDate}
        endDate={receiptEndDate}
        onStartDateChange={setReceiptStartDate}
        onEndDateChange={setReceiptEndDate}
        onGenerate={previewReceipt}
        isPreviewing={receiptPreviewLoading}
      />
      <div className="container-main space-y-4">
        <div className="flex flex-1 justify-between">
          {donorQuery.data && (
            <div className="[&>div]:space-x-2 [&>div]:text-xl space-y-1">
              {/* Email row */}
              <div className="!text-2xl font-semibold flex items-center gap-2">
                <EmailOutlinedIcon />
                {!isEditing ? (
                  <>
                    <label>{donorQuery.data.email}</label>
                    {donorQuery.data.email_status &&
                      donorQuery.data.email_status !== "valid" && (
                        <Chip
                          size="small"
                          icon={<WarningAmberOutlinedIcon />}
                          label={donorQuery.data.email_status}
                          color={
                            donorQuery.data.email_status === "bounced" ? "error" : "warning"
                          }
                          sx={{ ml: 1 }}
                        />
                      )}
                  </>
                ) : (
                  <TextField
                    label={t("Email")}
                    type="email"
                    size="small"
                    value={form?.email || ""}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    sx={{ minWidth: 320 }}
                  />
                )}
              </div>

              {/* Name */}
              <div className="text-xl flex items-center gap-2">
                <PersonOutlinedIcon />
                {!isEditing ? (
                  <label>
                    {donorQuery.data.first_name && donorQuery.data.last_name
                      ? `${donorQuery.data.first_name} ${donorQuery.data.last_name}`
                      : ""}
                  </label>
                ) : (
                  <div className="flex gap-2">
                    <TextField
                      size="small"
                      label={t("First name")}
                      value={form?.first_name || ""}
                      onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                    />
                    <TextField
                      size="small"
                      label={t("Last name")}
                      value={form?.last_name || ""}
                      onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                    />
                  </div>
                )}
              </div>

              {/* Phone */}
              <div className="text-xl flex items-center gap-2">
                <PhoneOutlinedIcon />
                {!isEditing ? (
                  <label>{donorQuery.data.phone}</label>
                ) : (
                  <TextField
                    size="small"
                    label={t("Phone")}
                    value={form?.phone || ""}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  />
                )}
              </div>

              {/* Address */}
              <div className="text-xl flex items-center gap-2">
                <HomeOutlinedIcon />
                {!isEditing ? (
                  <label>{donorQuery.data.adresse}</label>
                ) : (
                  <TextField
                    size="small"
                    label={t("Address")}
                    value={form?.adresse || ""}
                    onChange={(e) => setForm((f) => ({ ...f, adresse: e.target.value }))}
                    sx={{ minWidth: 320 }}
                  />
                )}
              </div>

              {/* Company */}
              <div className="text-xl flex items-center gap-2">
                <WorkOutlineOutlinedIcon />
                {!isEditing ? (
                  <label>{donorQuery.data.company}</label>
                ) : (
                  <TextField
                    size="small"
                    label={t("Company")}
                    value={form?.company || ""}
                    onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                  />
                )}
              </div>
            </div>
          )}

          <div className="flex flex-col space-y-4">
            <div className="flex space-x-4 w-full justify-end">
              {!isEditing ? (
                <Button variant="outlined" onClick={() => setIsEditing(true)}>
                  {t("edit_profile")}
                </Button>
              ) : (
                <>
                  <Button
                    variant="contained"
                    onClick={() =>
                      updateDonorMutation.mutate({
                        id: donorQuery.data._id,
                        body: { ...form },
                      })
                    }
                    disabled={updateDonorMutation.isPending}
                  >
                    {t("save")}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      setIsEditing(false);
                      // reset to server values
                      if (donorQuery.data) {
                        setForm({
                          first_name: donorQuery.data.first_name || "",
                          last_name: donorQuery.data.last_name || "",
                          email: donorQuery.data.email || "",
                          phone: donorQuery.data.phone || "",
                          adresse: donorQuery.data.adresse || "",
                          company: donorQuery.data.company || "",
                          zip_code: donorQuery.data.zip_code || "",
                          organization_id:
                            donorQuery.data.organization_id || undefined,
                        });
                      }
                    }}
                  >
                    {t("cancel")}
                  </Button>
                </>
              )}

              {/* Resend link is now always available */}
              <Button
                variant="outlined"
                onClick={() =>
                  resendLinkMutation.mutate({
                    id: donorQuery.data._id,
                  })
                }
                disabled={resendLinkMutation.isPending || !donorQuery.data}
              >
                {t("resend_profile_link")}
              </Button>

              <Select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                size="small"
              >
                {yearOptions.map((year, index) => (
                  <MenuItem value={year} key={index}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
              <ButtonLoading
                loading={generateReceiptMutation.isPending}
                variant="contained"
                onClick={handleOpenReceiptCampaignDialog}
              >
                {t("donor_profile_generateReceipt_text")}
              </ButtonLoading>
            </div>
            {lastCurrentYearReceiptStatus?.created_at ? (
              <div className="w-[250px] items-end">
                {t("donor_profile_generatedOn_text")}{" "}
                <span className="font-semibold">
                  {dateTimeReadable(
                    new Date(lastCurrentYearReceiptStatus?.created_at)
                  )}
                </span>{" "}
                {t("donor_profile_with_text")}{" "}
                <span
                  className="font-semibold"
                  style={{
                    color: lastCurrentYearReceiptStatus.success
                      ? theme.palette.success.main
                      : theme.palette.error.main,
                  }}
                >
                  {lastCurrentYearReceiptStatus.success
                    ? t("donor_profile_success_text")
                    : t("donor_profile_error_text")}
                </span>
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>
        <Divider />
        <div>
          <LoadingContainer>
            <div className={"flex flex-col flex-1"}>
              <Tabs value={tabValue} onChange={handleTabChange}>
                <Tab label={t("donor_profile_donationHistory_text")} />
                <Tab label={t("donor_profile_listOfReceipts_text")} />
                <Tab label={tPM("payment_methods_tab_label")} />
                <Tab label={t("promises", "Promises")} />
              </Tabs>
              {tabValue === 0 ? (
                <>
                  <div className="flex flex-1 h-full flex-col space-y-4">
                    <div className={"flex justify-end"}>
                      <Tooltip title={""} arrow>
                        <span>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                              setAddCashDialogOpen(true);
                            }}
                          >
                            {t("donor_profile_addCash_button")}{" "}
                          </Button>
                        </span>
                      </Tooltip>
                    </div>
                    <DataGrid
                      columns={columns}
                      rows={
                        transactionsQuery.data
                          ? transactionsQuery.data.map((transaction) => ({
                              id: transaction._id,
                              amount: `${numberWithCommas(
                                transaction.amount
                              )}$`,
                              campaignName: transaction.campaign?.name,
                              payment_type: transaction.payment_type,
                              isRefund: transaction.amount < 0,
                              date: dateTimeReadableSimple(
                                transaction.transactionDate
                              ),
                            }))
                          : []
                      }
                      filters={filters}
                      loading={
                        transactionsQuery.isLoading ||
                        campaignFilterQuery.isLoading
                      }
                    />
                  </div>
                </>
              ) : (
                <></>
              )}
              {tabValue === 1 ? (
                <>
                  <div className="flex flex-1 h-screen mt-4">
                        <ReceiptsByYearTable
                          data={donorReceiptsGroupedQuery.data}
                          loading={donorReceiptsGroupedQuery.isLoading || donorReceiptsGroupedQuery.isFetching}
                          onView={handleViewReceipt}
                          onDelete={handleDeleteReceipt}
                          onRestore={handleRestoreReceipt}
                          onToggleValid={handleToggleValid}
                          deleting={softDeleteReceiptMutation.isPending}
                          restoring={restoreReceiptMutation.isPending}
                          toggling={setReceiptValidityMutation.isPending}
                        />
                  </div>
                </>
              ) : (
                <></>
              )}
              {tabValue === 2 && donorQuery.data ? (
                <div className="mt-4">
                  <PaymentMethodsTab
                    donorId={donorQuery.data._id}
                    organizationId={authContext.authContext.accessToken.organization_id}
                    donorEmail={donorQuery.data.email}
                    actingUserId={authContext.authContext.accessToken.id}
                    onTransactionsChanged={() => transactionsQuery.refetch()}
                  />
                </div>
              ) : null}
              {tabValue === 3 && donorQuery.data ? (
                <PromiseFollowUpTab
                  donorId={donorQuery.data._id}
                  organizationId={authContext.authContext.accessToken.organization_id}
                />
              ) : null}
            </div>
          </LoadingContainer>
        </div>
      </div>
      <Dialog
        open={pdfDialogOpen}
        onClose={() => {
          setPdfDialogOpen(false);
          if (pdfBlobUrl && pdfBlobUrl.startsWith("blob:")) URL.revokeObjectURL(pdfBlobUrl);
          setPdfBlobUrl(null);
          if (receiptPreviewData) {
            setReceiptPreviewData(null);
            setReceiptCampaignDialogOpen(true);
          }
        }}
        fullWidth
        maxWidth="xl"
        PaperProps={{ style: { height: "90vh" } }}
      >
        <DialogTitle>
          <div className="flex items-center justify-between">
            <span className="text-lg font-semibold">
              {receiptPreviewData ? t("donor_receipt_preview_title", "Receipt preview") : ""}
            </span>
            <IconButton
              onClick={() => {
                setPdfDialogOpen(false);
                if (pdfBlobUrl && pdfBlobUrl.startsWith("blob:")) URL.revokeObjectURL(pdfBlobUrl);
                setPdfBlobUrl(null);
                if (receiptPreviewData) {
                  setReceiptPreviewData(null);
                  setReceiptCampaignDialogOpen(true);
                }
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers style={{ height: "100%" }}>
          {pdfBlobUrl ? (
            <iframe
              src={pdfBlobUrl}
              title="Receipt PDF"
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          ) : (
            <Typography>{t("donor_profile_receipt_no_pdf")}</Typography>
          )}
        </DialogContent>
        {receiptPreviewData ? (
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => {
                setPdfDialogOpen(false);
                if (pdfBlobUrl && pdfBlobUrl.startsWith("blob:")) URL.revokeObjectURL(pdfBlobUrl);
                setPdfBlobUrl(null);
                setReceiptPreviewData(null);
                setReceiptCampaignDialogOpen(true);
              }}
            >
              {t("cancel", "Cancel")}
            </Button>
            <ButtonLoading
              loading={generateReceiptMutation.isPending}
              variant="contained"
              onClick={generateReceipt}
            >
              {t("donor_receipt_confirm_send", "Confirm & Send")}
            </ButtonLoading>
          </DialogActions>
        ) : null}
      </Dialog>
    </LoadingContainer>
  );
};

export default Donor;
